import config from "../data/config";
import { fetchWithTimeout } from "../utils/httpClient";
import type {
  CreateDeploymentPayload,
  CreateDsAppPayload,
  Deployment,
  DeploymentDetails,
  DeploymentLogItem,
  DeploymentStats,
  DeploymentTemplate,
  DeploymentTemplatesListResponse,
  DsApp,
  PaginatedList,
  ParameterTemplateResponse,
  ThirdPartyContract,
  ThirdPartyContractDetails,
} from "../types/pdm.types";
import {
  mockCreateDeployment,
  mockCreateDsApp,
  mockGetCommitmentLevels,
  mockGetDeployment,
  mockGetDeploymentLogsPage,
  mockGetDeploymentTemplate,
  mockGetDeploymentTemplates,
  mockGetDeployments,
  mockGetDeploymentStats,
  mockGetDsApp,
  mockGetDsApps,
  mockGetParameterTemplates,
  mockGetTemplates,
  mockGetThirdPartyContract,
  mockGetThirdPartyContracts,
  mockRetryDeployment,
} from "../mocks/pdmMocks";

const JSON_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function formatHttpErrorBody(text: string, fallback: string): string {
  if (!text) {
    return fallback;
  }
  try {
    const body = JSON.parse(text) as {
      message?: string | string[];
    };
    if (Array.isArray(body.message)) {
      return body.message.join(" ");
    }
    if (typeof body.message === "string") {
      return body.message;
    }
  } catch {
    // use raw text
  }
  return text;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!response.ok) {
    const fallback = response.statusText || "Request failed";
    throw new Error(formatHttpErrorBody(text, fallback));
  }
  if (!text) {
    return {} as T;
  }
  return JSON.parse(text) as T;
}

function buildUrl(path: string, searchParams?: Record<string, string>) {
  const base = config.PDM_API_BASE_URL.replace(/\/$/, "");
  const url = new URL(`${base}${path}`);
  if (searchParams) {
    Object.entries(searchParams).forEach(([k, v]) => {
      if (v !== undefined && v !== "") {
        url.searchParams.set(k, v);
      }
    });
  }
  return url.toString();
}

export async function getDsApps(params: {
  offset: number;
  limit: number;
  name?: string;
}): Promise<PaginatedList<DsApp>> {
  if (config.USE_PDM_MOCKS) {
    return mockGetDsApps(params.offset, params.limit, params.name ?? "");
  }
  const url = buildUrl("/protocols", {
    offset: String(params.offset),
    limit: String(params.limit),
    ...(params.name?.trim()
      ? { name: params.name.trim() }
      : {}),
  });
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  return parseJson<PaginatedList<DsApp>>(res);
}

export async function getDsApp(id: string): Promise<DsApp> {
  if (config.USE_PDM_MOCKS) {
    return mockGetDsApp(id);
  }
  const url = buildUrl(`/protocols/${id}`);
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  return parseJson<DsApp>(res);
}

export async function createDsApp(
  payload: CreateDsAppPayload,
): Promise<DsApp> {
  if (config.USE_PDM_MOCKS) {
    return mockCreateDsApp(payload);
  }
  const url = buildUrl("/protocols");
  const res = await fetchWithTimeout(url, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  return parseJson<DsApp>(res);
}

export async function getTemplates(
  dsAppId: string,
  params: { offset: number; limit: number },
): Promise<PaginatedList<DeploymentTemplate>> {
  if (config.USE_PDM_MOCKS) {
    return mockGetTemplates(dsAppId, params.offset, params.limit);
  }
  const url = buildUrl(`/protocols/${dsAppId}/templates`, {
    offset: String(params.offset),
    limit: String(params.limit),
  });
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  return parseJson<PaginatedList<DeploymentTemplate>>(res);
}

export async function getDeploymentTemplates(params: {
  offset: number;
  limit: number;
}): Promise<PaginatedList<DeploymentTemplate>> {
  if (config.USE_PDM_MOCKS) {
    const data = await mockGetDeploymentTemplates(params.offset, params.limit);
    return {
      total: data.total,
      items: data.items.map((item) => normalizeDeploymentTemplate(item)),
    };
  }
  const url = buildUrl("/deployment-templates", {
    offset: String(params.offset),
    limit: String(params.limit),
  });
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  const data = await parseJson<PaginatedList<DeploymentTemplate>>(res);
  return {
    total: data.total,
    items: data.items.map((item) => normalizeDeploymentTemplate(item)),
  };
}

export async function getDeploymentTemplatesList(params: {
  offset: number;
  limit: number;
}): Promise<DeploymentTemplatesListResponse> {
  if (config.USE_PDM_MOCKS) {
    const data = await mockGetDeploymentTemplates(params.offset, params.limit);
    return {
      total: data.total,
      items: data.items.map((t) => ({
        id: t.id,
        name: t.name,
        contractName: t.contractName,
        deploymentTask: t.deploymentTask,
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
      })),
    };
  }

  const url = buildUrl("/deployment-templates", {
    offset: String(params.offset),
    limit: String(params.limit),
  });
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  return parseJson<DeploymentTemplatesListResponse>(res);
}

function normalizeDeploymentTemplate(
  template: DeploymentTemplate,
): DeploymentTemplate {
  const raw = template.postDeploymentInstructions;
  const postDeploymentInstructions =
    raw === null || raw === undefined
      ? raw
      : Array.isArray(raw)
        ? raw
        : [];
  return { ...template, postDeploymentInstructions };
}

export async function getDeploymentTemplate(
  id: string,
): Promise<DeploymentTemplate> {
  if (config.USE_PDM_MOCKS) {
    return mockGetDeploymentTemplate(id);
  }
  const url = buildUrl(`/deployment-templates/${id}`);
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  const data = await parseJson<DeploymentTemplate>(res);
  return normalizeDeploymentTemplate(data);
}

export async function getParameterTemplates(params: {
  deploymentTemplateId: string;
  network: string;
  offset: number;
  limit: number;
}): Promise<ParameterTemplateResponse> {
  if (config.USE_PDM_MOCKS) {
    return mockGetParameterTemplates(
      params.deploymentTemplateId,
      params.network,
      params.offset,
      params.limit,
    );
  }
  const url = buildUrl("/parameter-templates", {
    deploymentTemplateId: params.deploymentTemplateId,
    networkType: config.NETWORK_TYPE,
    network: params.network,
    offset: String(params.offset),
    limit: String(params.limit),
  });
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  return parseJson<ParameterTemplateResponse>(res);
}

export async function getThirdPartyContracts(filters: {
  network?: string;
  networkType?: string;
}): Promise<ThirdPartyContract[]> {
  if (config.USE_PDM_MOCKS) {
    return mockGetThirdPartyContracts(filters.network, filters.networkType);
  }
  const url = buildUrl("/third-party-contracts", {
    ...(filters.network ? { network: filters.network } : {}),
    ...(filters.networkType ? { networkType: filters.networkType } : {}),
  });
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  return parseJson<ThirdPartyContract[]>(res);
}

export async function getThirdPartyContract(
  id: string,
): Promise<ThirdPartyContractDetails> {
  if (config.USE_PDM_MOCKS) {
    return mockGetThirdPartyContract(id);
  }
  const url = buildUrl(`/third-party-contracts/${encodeURIComponent(id)}`);
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  return parseJson<ThirdPartyContractDetails>(res);
}

export async function createDeployment(
  payload: CreateDeploymentPayload,
): Promise<Deployment> {
  if (config.USE_PDM_MOCKS) {
    return mockCreateDeployment(payload);
  }
  const url = buildUrl("/deployments");
  const res = await fetchWithTimeout(url, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  });
  return parseJson<Deployment>(res);
}

export async function getDeployments(params: {
  offset: number;
  limit: number;
  status?: string;
  network?: string;
  deploymentTemplateId?: string;
}): Promise<PaginatedList<Deployment>> {
  if (config.USE_PDM_MOCKS) {
    return mockGetDeployments(params.offset, params.limit, {
      status: params.status,
      network: params.network,
      deploymentTemplateId: params.deploymentTemplateId,
    });
  }
  const url = buildUrl("/deployments", {
    offset: String(params.offset),
    limit: String(params.limit),
    status: params.status ?? "",
    network: params.network ?? "",
    deploymentTemplateId: params.deploymentTemplateId ?? "",
  });
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  return parseJson<PaginatedList<Deployment>>(res);
}

export async function getDeployment(id: string): Promise<DeploymentDetails> {
  if (config.USE_PDM_MOCKS) {
    return mockGetDeployment(id);
  }
  const url = buildUrl(`/deployments/${id}`);
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  return parseJson<DeploymentDetails>(res);
}

export async function getDeploymentStats(): Promise<DeploymentStats> {
  if (config.USE_PDM_MOCKS) {
    return mockGetDeploymentStats();
  }
  const { items } = await getDeployments({ offset: 0, limit: 100 });
  return {
    totalSuccess: items.filter((d) => d.status === "success").length,
    totalPending: items.filter((d) => d.status === "pending").length,
    inProgress: items.filter((d) => d.status === "in_progress").length,
  };
}

export async function getDeploymentLogsPage(
  deploymentId: string,
  params: { offset: number; limit: number },
): Promise<PaginatedList<DeploymentLogItem>> {
  if (config.USE_PDM_MOCKS) {
    return mockGetDeploymentLogsPage(deploymentId, params.offset, params.limit);
  }
  const url = buildUrl(`/deployments/${deploymentId}/logs`, {
    offset: String(params.offset),
    limit: String(params.limit),
  });
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  return parseJson<PaginatedList<DeploymentLogItem>>(res);
}

export type CommitmentLevel = { value: number; level: string };

export async function getCommitmentLevels(
  network: string,
): Promise<CommitmentLevel[]> {
  if (config.USE_PDM_MOCKS) {
    return mockGetCommitmentLevels();
  }
  const url = buildUrl("/bridges/commitment-levels", { network });
  const res = await fetchWithTimeout(url, {
    headers: { Accept: "application/json" },
  });
  return parseJson<CommitmentLevel[]>(res);
}

export async function retryDeployment(
  deploymentId: string,
  options?: { keepalive?: boolean },
): Promise<{ status?: string; message?: string }> {
  if (config.USE_PDM_MOCKS) {
    await mockRetryDeployment(deploymentId);
    return { status: "ok" };
  }
  const url = buildUrl(`/deployments/${deploymentId}/retry`);
  const res = await fetchWithTimeout(url, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({}),
    keepalive: options?.keepalive ?? false,
  });
  return parseJson<{ status?: string; message?: string }>(res);
}

/**
 * Start a retry request without enforcing the global 10s timeout.
 * The backend may take a long time to complete the retry workflow, but the UI
 * only needs the initial acknowledgment (or a skipped response).
 */
export async function startRetryDeployment(
  deploymentId: string,
  options?: { keepalive?: boolean },
): Promise<{ status?: string; message?: string }> {
  if (config.USE_PDM_MOCKS) {
    await mockRetryDeployment(deploymentId);
    return { status: "ok" };
  }
  const url = buildUrl(`/deployments/${deploymentId}/retry`);
  const res = await fetch(url, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({}),
    keepalive: options?.keepalive ?? false,
  });
  return parseJson<{ status?: string; message?: string }>(res);
}
